/* eslint-disable @typescript-eslint/camelcase */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { trackings } from '../tracking';
import { IconCoreSearch } from '@xingternal/malt-icon';
import { trackingURL } from '@xingternal/xing-view/xing-tracking';
import classNames from 'classnames';
import styles from './SearchSuggestions.css';

const flatten = arr => [].concat(...arr);

const sanitize = str => str.replace(/<\/?phrase>/gi, '');

const SuggestionType = PropTypes.shape({
  title: PropTypes.string,
  subtitle: PropTypes.string,
  url: PropTypes.string.isRequired,
  thumbnail: PropTypes.string.isRequired,
  trackingInfo: PropTypes.shape({
    consumer: PropTypes.string,
    position: PropTypes.number,
    service: PropTypes.string,
    trackingToken: PropTypes.string,
  }),
});

const SectionType = PropTypes.shape({
  suggestions: PropTypes.arrayOf(SuggestionType).isRequired,
  name: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
});

const Title = ({ phrase }) => {
  const reg = /<phrase>(.*?)<\/phrase>/gim;
  const dereg = s => `<phrase>${s}</phrase>`;

  const matches = phrase.match(reg) || [];
  const label = phrase.split(reg);

  const expression = label.filter(Boolean).map((m, i) => (
    <Fragment key={i}>
      {matches.includes(dereg(m)) ? (
        <span className={styles.itemMatch} data-qa="item-match">
          {m}
        </span>
      ) : (
        m
      )}
    </Fragment>
  ));

  return <div className={styles.itemTitle}>{expression}</div>;
};

Title.propTypes = {
  phrase: PropTypes.string.isRequired,
};

class Item extends Component {
  static propTypes = {
    data: SuggestionType.isRequired,
    getItemProps: PropTypes.func.isRequired,
    isHighlighted: PropTypes.bool.isRequired,
    isQuerySuggestion: PropTypes.bool.isRequired,
    userId: PropTypes.string.isRequired,
    className: PropTypes.string,
  };

  render() {
    const sanitizedTitle = sanitize(this.props.data.title);

    const {
      consumer,
      service,
      trackingToken,
      position,
    } = this.props.data.trackingInfo;
    const url = trackingURL(this.props.data.url, 'result')(trackings);

    return (
      <a
        {...this.props.getItemProps({
          item: {
            title: sanitizedTitle,
            url: url,
            trackingData: {
              interaction_type: 'view',
              consumer,
              service,
              tracking_token: trackingToken,
              position,
              user_id: this.props.userId,
            },
          },
          href: url,
          className: classNames(
            styles.item,
            this.props.isHighlighted && styles.highlighted,
            this.props.isQuerySuggestion && styles.querySuggestionAction
          ),
        })}
      >
        {this.props.isQuerySuggestion ? (
          <div className={styles.querySuggestionImage}>
            <IconCoreSearch size={20} />
          </div>
        ) : (
          <img
            src={this.props.data.thumbnail}
            className={styles.itemImage}
            alt={sanitizedTitle}
            data-qa="item-image"
          />
        )}

        <article className={styles.itemContent}>
          <Title phrase={this.props.data.title} />
          <p className={styles.itemSubtitle}>
            {this.props.data.subtitle || ' '}
          </p>
        </article>
      </a>
    );
  }
}

class SearchSuggestionsTracking extends Component {
  static propTypes = {
    suggestions: PropTypes.arrayOf(SectionType),
    getItemProps: PropTypes.func.isRequired,
    userId: PropTypes.string.isRequired,
    searchLink: PropTypes.func.isRequired,
    highlightedIndex: PropTypes.number,
    executeSearchTracking: PropTypes.func,
    query: PropTypes.string,
  };

  componentDidMount() {
    this.props.suggestions.length &&
      this.props.executeSearchTracking &&
      this.props.executeSearchTracking(
        this.formDSTrackingData(this.filteredSections(this.props.suggestions))
      );
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.executeSearchTracking &&
      JSON.stringify(prevProps.suggestions) !==
        JSON.stringify(this.props.suggestions)
    ) {
      this.props.executeSearchTracking(
        this.formDSTrackingData(this.filteredSections(this.props.suggestions))
      );
    }
  }

  filteredSections = suggestions =>
    suggestions.filter(section => section.suggestions.length > 0);

  formDSTrackingData = sections => {
    return flatten(
      sections.map(section => {
        return section.suggestions.map(suggestion => {
          const {
            consumer,
            service,
            trackingToken,
            position,
          } = suggestion.trackingInfo;

          return {
            consumer,
            service,
            trackingToken,
            position,
            interactionType: 'shown',
            userId: this.props.userId,
          };
        });
      })
    );
  };

  render() {
    return (
      <SearchSuggestions
        highlightedIndex={this.props.highlightedIndex}
        getItemProps={this.props.getItemProps}
        suggestions={this.filteredSections(this.props.suggestions)}
        searchLink={this.props.searchLink}
        query={this.props.query}
        userId={this.props.userId}
      />
    );
  }
}

const SearchSuggestions = injectIntl(
  ({
    suggestions = [],
    getItemProps,
    highlightedIndex,
    searchLink,
    query,
    userId,
    intl,
  }) => {
    const translations = {
      members: 'VERTICAL_NAVIGATION_HEADER_SEARCH_MEMBERS',
      jobs: 'VERTICAL_NAVIGATION_HEADER_SEARCH_JOBS',
      events: 'VERTICAL_NAVIGATION_HEADER_SEARCH_EVENTS',
      news: 'VERTICAL_NAVIGATION_HEADER_SEARCH_NEWS',
      companies: 'VERTICAL_NAVIGATION_HEADER_SEARCH_COMPANIES_PROFILE',
      communities: 'VERTICAL_NAVIGATION_HEADER_SEARCH_COMMUNITIES',
    };

    let index = 0;

    return (
      suggestions && (
        <div data-qa="search-suggestions">
          {suggestions.map(section => (
            <section
              key={section.name}
              className={
                section.name === 'query_suggestions'
                  ? styles.querySuggestion
                  : null
              }
            >
              {section.suggestions.map(suggestion => {
                const data =
                  section.name === 'members' ||
                  section.name === 'query_suggestions'
                    ? suggestion
                    : {
                        ...suggestion,
                        subtitle: intl.formatMessage({
                          id: translations[section.name],
                        }),
                      };

                const isHighlighted = index === highlightedIndex;
                index++;

                return (
                  <Item
                    isQuerySuggestion={section.name === 'query_suggestions'}
                    getItemProps={getItemProps}
                    key={index + suggestion.url}
                    data={data}
                    isHighlighted={isHighlighted}
                    userId={userId}
                  />
                );
              })}
            </section>
          ))}
          <section className={styles.allResults}>
            <a
              className={styles.allResultsAction}
              href={searchLink({
                query: query,
                trackingKey: 'all_results_fallback',
              })}
            >
              <FormattedMessage id={'VERTICAL_NAVIGATION_HEADER_SEARCH_FOR'} />{' '}
              <strong>{query}</strong>
            </a>
          </section>
        </div>
      )
    );
  }
);

SearchSuggestions.propTypes = {
  suggestions: PropTypes.arrayOf(SectionType),
  getItemProps: PropTypes.func.isRequired,
  searchLink: PropTypes.func.isRequired,
  highlightedIndex: PropTypes.number,
  query: PropTypes.string,
  userId: PropTypes.string.isRequired,
  intl: intlShape,
};

SearchSuggestions.displayName = 'SearchSuggestions';

export default SearchSuggestionsTracking;
export { SearchSuggestionsTracking, SearchSuggestions, Title, Item };
